/**
 * Derived extraction schemas — D2 in the extraction defect ledger.
 *
 * Only 7 of ~26 requirements had a hand-written extraction schema, so a
 * perfectly good water-rights letter or wetland delineation uploaded against
 * any of the other 19 extracted NOTHING — not because extraction failed, but
 * because nobody had transcribed the checklist's authoredSchema into a
 * Gemini response schema by hand. The authoredSchema already declares every
 * field, its type, its unit and its enum values; this module derives the
 * response schema mechanically so every requirement with an authoredSchema
 * is extractable by construction, and a new checklist field is extractable
 * the day it is added.
 *
 * Derivation rules:
 *   - string / textarea / date → STRING (dates validated post-hoc by the
 *     sanitizer when the authoredSchema is threaded through — D6)
 *   - number  → NUMBER (unit folded into the description so the model
 *     reports in the schema's unit rather than inventing one)
 *   - boolean → BOOLEAN
 *   - enum    → STRING with the allowed values in the description; the
 *     sanitizer canonicalizes case and drops non-members (D6)
 *   - list    → ARRAY<STRING>
 *   - `notes` is SKIPPED — freeform prose is authored by humans, not
 *     extracted; a notes-only schema derives to `undefined`.
 *   - lineage keys (pageNumber / sectionHeading / excerptPreview) are
 *     appended EXCEPT for SENSITIVE_EXCERPT_REQUIREMENTS, where an excerpt
 *     could leak precise locations even when the structured fields are safe
 *     (the archaeology redaction precedent).
 *   - `required: []` ALWAYS. authoredSchema `required` means "the report
 *     needs this to read complete", not "every document must contain it" —
 *     attaching it to a response schema would mandate fabrication (D13).
 */
import { Type } from '../../services/geminiClient';
import type { AuthoredFieldDef } from '../../types/authoredEvidence';
import { checklistItemById } from '../../config/documentChecklists';
import type { RequirementDocExtractSchema } from './requirementDocExtractSchemas';
import { attributionSchemaProps } from '../sed/evidenceAttribution';

/**
 * Requirements whose excerpts must never be requested: even a short quote
 * can carry a coordinate or site description. Structured fields only.
 */
export const SENSITIVE_EXCERPT_REQUIREMENTS: ReadonlySet<string> = new Set([
  'archaeological-survey',
]);

function propertyFor(field: AuthoredFieldDef): Record<string, unknown> | null {
  const unitSuffix = field.unit ? ` Report the value in ${field.unit}.` : '';
  switch (field.type) {
    case 'number':
      return { type: Type.NUMBER, description: `${field.label}.${unitSuffix}` };
    case 'boolean':
      return { type: Type.BOOLEAN, description: `${field.label}.` };
    case 'enum': {
      const values = (field.enumValues ?? []).join(', ');
      return {
        type: Type.STRING,
        description: `${field.label}. One of: ${values}. Return exactly one of these values.`,
      };
    }
    case 'list':
      return {
        type: Type.ARRAY,
        items: { type: Type.STRING },
        description: `${field.label}.`,
      };
    case 'string':
    case 'textarea':
    case 'date':
      return { type: Type.STRING, description: `${field.label}.${unitSuffix}` };
    default:
      return null;
  }
}

/**
 * Derive a Gemini extraction schema from a checklist item's authoredSchema.
 * Returns `undefined` when the requirement has no checklist item, no
 * authoredSchema, or only a `notes` field — "nothing to extract" and
 * "no schema" must be the same answer so callers fall through identically.
 */
export function deriveRequirementDocExtractSchema(
  requirementId: string,
): RequirementDocExtractSchema | undefined {
  const item = checklistItemById(requirementId);
  const authored = item?.authoredSchema;
  if (!authored || authored.length === 0) return undefined;

  const properties: Record<string, unknown> = {};
  for (const field of authored) {
    if (field.key === 'notes') continue;
    const prop = propertyFor(field);
    if (prop) properties[field.key] = prop;
  }
  if (Object.keys(properties).length === 0) return undefined;

  properties.pageNumber = { type: Type.NUMBER };
  properties.sectionHeading = { type: Type.STRING };
  if (!SENSITIVE_EXCERPT_REQUIREMENTS.has(requirementId)) {
    properties.excerptPreview = { type: Type.STRING };
  }
  // P4 — stated attribution (who said it, for which project); read by the
  // extractor, never persisted as a field.
  Object.assign(properties, attributionSchemaProps(Type.STRING));

  return {
    requirementId,
    schema: { type: Type.OBJECT, properties },
    required: [],
  };
}
